import {
	Alert,
	Button,
	Group,
	Loader,
	Modal,
	Paper,
	Select,
	TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	createAdminDiscount,
	deleteAdminDiscount,
	discountKeys,
	listAdminDiscounts,
	listDiscountPositions,
	updateAdminDiscount,
} from "@/features/discounts/api/api";
import type {
	AdminDiscount,
	DiscountKind,
} from "@/features/discounts/api/types";
import { KIND_OPTIONS } from "@/features/discounts/lib/kind-options";
import {
	offerCreateBody,
	offerUpdateBody,
} from "@/features/discounts/lib/offer-payload";
import {
	emptyOfferValues,
	type OfferFormValues,
} from "@/features/discounts/lib/offer-schema";
import { showDiscountError } from "@/features/discounts/lib/use-discount-error";
import { OfferEditorForm } from "@/features/discounts/ui/offer-editor";
import { OffersTable } from "@/features/discounts/ui/offers-table";
import {
	listCorporatePartners,
	partnerKeys,
} from "@/features/partners/admin-partners";
import { describeApiErrorSync } from "@/shared/api/error-message";
import type { PageLimit } from "@/shared/api/types";
import { ConfirmDialog } from "@/shared/ui/confirm-dialog";

type OfferEditor = {
	id?: string;
	defaults: OfferFormValues;
};

export function OffersTab() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<DiscountKind | null>(null);
	const [editor, setEditor] = useState<OfferEditor | null>(null);
	const [pendingDelete, setPendingDelete] = useState<{
		id: string;
		name: string;
		isActive: boolean;
	} | null>(null);

	const listParams = {
		page,
		limit: 20 as PageLimit,
		search: search.trim().length >= 3 ? search.trim() : undefined,
		type: typeFilter ?? undefined,
	};

	const listQuery = useQuery({
		queryKey: discountKeys.list(listParams),
		queryFn: () => listAdminDiscounts(listParams),
	});

	const positionsQuery = useQuery({
		queryKey: discountKeys.positions({ limit: 100 }),
		queryFn: () => listDiscountPositions({ limit: 100 }),
	});

	const partnersQuery = useQuery({
		queryKey: partnerKeys.list({ limit: 100 }),
		queryFn: () => listCorporatePartners({ limit: 100 }),
		enabled: Boolean(editor),
	});

	const knownCategories = useMemo(() => {
		const map = new Map<string, string>();
		for (const offer of listQuery.data?.data ?? []) {
			for (const category of offer.categories) {
				map.set(
					category.id,
					category.code ? `${category.name} (${category.code})` : category.name,
				);
			}
		}
		return [...map.entries()].map(([value, label]) => ({ value, label }));
	}, [listQuery.data]);

	const saveMutation = useMutation({
		mutationFn: async (input: { id?: string; values: OfferFormValues }) => {
			if (input.id) {
				return updateAdminDiscount(input.id, offerUpdateBody(input.values));
			}
			return createAdminDiscount(offerCreateBody(input.values));
		},
		onSuccess: async () => {
			notifications.show({
				color: "green",
				message: t("discounts.offerSaved"),
			});
			setEditor(null);
			await queryClient.invalidateQueries({ queryKey: discountKeys.all });
		},
		onError: showDiscountError,
	});

	const deleteMutation = useMutation({
		mutationFn: deleteAdminDiscount,
		onSuccess: async () => {
			setPendingDelete(null);
			notifications.show({
				color: "red",
				message: t("discounts.offerDeleted"),
			});
			await queryClient.invalidateQueries({ queryKey: discountKeys.all });
		},
		onError: showDiscountError,
	});

	const openCreate = () => {
		const rates: OfferFormValues["rates"] = {};
		for (const position of positionsQuery.data?.data ?? []) {
			rates[position.id] = "";
		}
		setEditor({ defaults: { ...emptyOfferValues(), rates } });
	};

	const openEdit = (row: AdminDiscount) => {
		const rates: OfferFormValues["rates"] = {};
		for (const position of positionsQuery.data?.data ?? []) {
			rates[position.id] =
				row.rates.find((rate) => rate.positionId === position.id)?.percent ??
				"";
		}
		setEditor({
			id: row.id,
			defaults: {
				name: row.name,
				type: row.type,
				promoCode: row.promoCode ?? "",
				isActive: row.isActive,
				endsOn: row.endsOn ?? "",
				categoryIds: row.categories.map((category) => category.id),
				partnerIds: row.partners.map((partner) => partner.id),
				rates,
			},
		});
	};

	return (
		<Paper p="md" radius="lg" withBorder>
			<Group
				justify="space-between"
				mb="md"
				wrap="wrap"
				gap="sm"
				align="flex-end"
			>
				<Group wrap="wrap" gap="sm" style={{ flex: 1, minWidth: 0 }}>
					<TextInput
						placeholder={t("common.search")}
						value={search}
						onChange={(event) => {
							setSearch(event.currentTarget.value);
							setPage(1);
						}}
					/>
					<Select
						clearable
						placeholder={t("discounts.kind")}
						data={KIND_OPTIONS}
						value={typeFilter}
						onChange={(value) => {
							setTypeFilter((value as DiscountKind | null) ?? null);
							setPage(1);
						}}
					/>
				</Group>
				<Button onClick={openCreate}>{t("discounts.addOffer")}</Button>
			</Group>
			{listQuery.error ? (
				<Alert color="red" title={t("common.loadError")}>
					{describeApiErrorSync(listQuery.error)}
				</Alert>
			) : null}
			{listQuery.isLoading ? (
				<Loader />
			) : (
				<OffersTable
					rows={listQuery.data?.data ?? []}
					page={page}
					hasNextPage={Boolean(listQuery.data?.hasNextPage)}
					onPageChange={setPage}
					onEdit={openEdit}
					onDelete={(row) =>
						setPendingDelete({
							id: row.id,
							name: row.name,
							isActive: row.isActive,
						})
					}
				/>
			)}

			<Modal
				opened={Boolean(editor)}
				onClose={() => setEditor(null)}
				title={t("discounts.offer")}
				centered
				size="lg"
			>
				{editor ? (
					<OfferEditorForm
						key={editor.id ?? "new"}
						defaults={editor.defaults}
						existingId={editor.id}
						knownCategories={knownCategories}
						partnerOptions={(partnersQuery.data?.data ?? []).map((partner) => ({
							value: partner.id,
							label: partner.name,
						}))}
						positions={positionsQuery.data?.data ?? []}
						saving={saveMutation.isPending}
						onCancel={() => setEditor(null)}
						onSubmit={(values) =>
							saveMutation.mutate({ id: editor.id, values })
						}
					/>
				) : null}
			</Modal>

			<ConfirmDialog
				opened={Boolean(pendingDelete)}
				title={
					pendingDelete?.isActive
						? t("discounts.cannotDeleteActiveTitle")
						: t("discounts.confirmDeleteOfferTitle")
				}
				body={
					pendingDelete?.isActive
						? t("discounts.cannotDeleteActiveBody", {
								name: pendingDelete.name,
							})
						: t("discounts.confirmDeleteOfferBody", {
								name: pendingDelete?.name ?? "",
							})
				}
				loading={deleteMutation.isPending}
				onCancel={() => setPendingDelete(null)}
				onConfirm={
					pendingDelete && !pendingDelete.isActive
						? () => deleteMutation.mutate(pendingDelete.id)
						: undefined
				}
			/>
		</Paper>
	);
}
